import { useEffect, useRef } from 'react';

const SERVICES = [
  { ko: '눈썹 문신', en: 'Eyebrow', zh: '纹眉', ja: '眉アート' },
  { ko: '아이라인 문신', en: 'Eyeliner', zh: '眼线', ja: 'アイライン' },
  { ko: '입술 문신', en: 'Lip Tattoo', zh: '唇纹', ja: 'リップ' },
  { ko: '두피 문신', en: 'Scalp SMP', zh: '头皮纹身', ja: 'スキャルプ' },
];

const STEPS = [
  { num: '01', title: '무료 상담', desc: '피부 타입, 얼굴형, 원하는 디자인을 함께 분석합니다.' },
  { num: '02', title: '맞춤 디자인', desc: '고객님의 골격과 표정에 맞는 1:1 전용 라인을 설계합니다.' },
  { num: '03', title: '시술 진행', desc: '협회 공인 전문가가 저자극 안료로 정밀하게 시술합니다.' },
  { num: '04', title: '쑥좌욕 케어', desc: '시술 후 쑥의 온열 케어로 회복과 색소 정착을 강화합니다.' },
];

export default function SemiPermanentSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal, .section-reveal-left, .section-reveal-right, .section-reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="semi-permanent" ref={ref} className="bg-white py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">Semi-Permanent Makeup · 半永久化妆 · アートMake-up</span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">반영구 문신</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            정밀한 예술적 감각과 최신 기법으로 당신만의 자연스럽고 완벽한 라인을 완성합니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-14 md:gap-18 items-start">
          {/* Left Content */}
          <div className="section-reveal-left order-2 lg:order-1 flex flex-col gap-6">
            {/* 쑥좌욕 케어 강조 */}
            <div className="bg-gradient-to-r from-gold-pale to-cream border border-gold/30 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-gold rounded-full text-white flex-shrink-0">
                  <i className="ri-leaf-line text-xl"></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-lg font-bold text-gray-900">쑥좌욕 케어 포함</span>
                    <span className="bg-plum text-white text-xs px-2 py-0.5 rounded-full font-sans font-semibold whitespace-nowrap">차별화 서비스</span>
                  </div>
                  <p className="font-sans text-xs text-plum font-medium mb-2">Mugwort Steaming Care Included · 艾灸护理包含 · よもぎ蒸し</p>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed">
                    반영구 눈썹 문신 시술 후 <strong>쑥좌욕 케어</strong>가 포함됩니다. 쑥의 항균 · 항염 성분이 시술 부위 회복을 돕고, 한방의 온열 에너지가 색소 정착을 강화합니다. 타 클리닉과는 차별화된 헤라만의 특별한 케어입니다.
                  </p>
                </div>
              </div>
            </div>

            {/* Service List */}
            <div className="grid grid-cols-2 gap-4">
              {SERVICES.map((s, i) => (
                <div
                  key={s.ko}
                  className="section-reveal-scale bg-cream border border-gold/15 rounded-xl p-4 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300 cursor-default"
                  style={{ transitionDelay: `${0.08 + i * 0.1}s` }}
                >
                  <p className="font-serif text-base font-bold text-gray-900 mb-1">{s.ko}</p>
                  <p className="font-sans text-xs text-gold-dark font-medium">{s.en}</p>
                  <p className="font-sans text-xs text-gray-400">{s.zh} · {s.ja}</p>
                </div>
              ))}
            </div>

            {/* Price Highlight */}
            <div className="bg-plum rounded-2xl p-5 text-white flex items-center justify-between gap-3">
              <div>
                <p className="font-sans text-sm text-white/70 mb-0.5">이벤트 특가 · Event Price</p>
                <p className="font-serif text-2xl font-bold">250,000원</p>
                <p className="font-sans text-xs text-white/60 mt-1">쑥 좌욕 포함 · Mugwort Steaming Included</p>
              </div>
              <button
                onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="flex-shrink-0 bg-gold text-white px-4 sm:px-5 py-2.5 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer hover:bg-gold-dark transition-colors duration-200"
              >
                예약하기
              </button>
            </div>

            {/* 시술 프로세스 */}
            <div className="border border-gold/15 rounded-2xl p-5">
              <p className="font-serif text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-list-check text-gold text-base"></i>
                </span>
                시술 프로세스
              </p>
              <div className="flex flex-col gap-3">
                {STEPS.map((step, i) => (
                  <div key={step.num} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gold/10 border border-gold/30">
                      <span className="font-sans text-xs font-bold text-gold-dark">{step.num}</span>
                    </div>
                    <div className="pt-0.5">
                      <p className="font-sans text-sm font-semibold text-gray-900 mb-0.5">{step.title}</p>
                      <p className="font-sans text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="absolute" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Gallery — slides in from right */}
          <div className="section-reveal-right order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-2.5">
              {/* 왼쪽 상단 */}
              <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 md:h-72 group col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/a2867867-5eec-49ac-8b03-e0e5d401d061_1774574779303.jpg?v=4d6ba7abcb32befbe5fa713d446d9811"
                  alt="반영구 눈썹 문신 시술 예시 1"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <div className="bg-white/95 backdrop-blur-sm border border-gold/30 rounded-lg px-2.5 py-1.5">
                    <p className="font-sans text-xs text-gold-dark font-bold whitespace-nowrap"># 협회 공인</p>
                  </div>
                </div>
              </div>

              {/* 오른쪽 상단 */}
              <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 md:h-72 group col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/3432c733-7e85-49c8-976e-899919ae566c_1774574778184.jpg?v=a31cda4492b8aea3efe095ba9e53eb63"
                  alt="반영구 눈썹 문신 시술 예시 2"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>

              {/* 왼쪽 하단 */}
              <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 md:h-72 group col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/ff5fef2d-8b42-4ecf-a76f-4e72c3ac910d_1774574777036.jpg?v=2a60f2409df43877c26928725d13a1a0"
                  alt="반영구 눈썹 문신 시술 예시 3"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>

              {/* 오른쪽 하단 */}
              <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 md:h-72 group col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/0ba5950d-14ed-44e1-ab6f-00aa400e8199_1774574775385.jpg?v=91f1ac8adfac68605859fc18bc031ca6"
                  alt="반영구 눈썹 문신 시술 예시 4"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 right-3">
                  <div className="bg-white/95 backdrop-blur-sm border border-gold/30 rounded-lg px-2.5 py-1.5">
                    <p className="font-sans text-xs text-gold-dark font-bold whitespace-nowrap">실제 시술 사진</p>
                  </div>
                </div>
              </div>

              {/* 추가 이미지 1 */}
              <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 md:h-72 group col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/4930dbfc-a617-4dd6-911d-b9aa73ea2816_KakaoTalk_20260402_172815584.jpg?v=4f15da8d6ed14770d7075e920cc77d45"
                  alt="반영구 눈썹 문신 시술 예시 5"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>

              {/* 추가 이미지 2 */}
              <div className="relative rounded-2xl overflow-hidden h-52 sm:h-64 md:h-72 group col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/26978452-fc40-436a-9e05-cdbcef7473ae_KakaoTalk_20260402_172824636_01.jpg?v=a6d2991092221dac7c634d24b78176e0"
                  alt="반영구 눈썹 문신 시술 예시 6"
                  className="w-full h-full object-cover object-bottom transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
              </div>

              {/* Before & After — 2열 전체 */}
              <div className="col-span-2 mt-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-gold/20"></div>
                  <span className="font-sans text-xs text-gold-dark font-semibold tracking-widest uppercase whitespace-nowrap">Before &amp; After</span>
                  <div className="h-px flex-1 bg-gold/20"></div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {/* Before */}
                  <div className="relative rounded-2xl overflow-hidden h-56 sm:h-64 md:h-72 group">
                    <img
                      src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/f925983c-91df-41bb-aaa2-2ab3baec9b62_KakaoTalk_20260327_174639146.jpg?v=89983778bdc11cdc5f24b8ae36b834af"
                      alt="반영구 눈썹 문신 시술 전"
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                    <div className="absolute bottom-3 left-3">
                      <span className="font-sans text-xs font-bold text-white bg-gray-700/80 backdrop-blur-sm px-3 py-1 rounded-full tracking-widest uppercase whitespace-nowrap">Before</span>
                    </div>
                  </div>
                  {/* After */}
                  <div className="relative rounded-2xl overflow-hidden h-56 sm:h-64 md:h-72 group">
                    <img
                      src="https://storage.readdy-site.link/project_files/d3e20c33-90eb-45ba-8644-8f19c58dcc82/5da475fa-3d28-4429-90b1-f3b2b4e3350e_KakaoTalk_20260327_174639146_01.jpg?v=c6f6d825e53b55633003630585888d40"
                      alt="반영구 눈썹 문신 시술 후"
                      className="w-full h-full object-cover object-bottom transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                    <div className="absolute bottom-3 right-3">
                      <span className="font-sans text-xs font-bold text-white bg-gold/90 backdrop-blur-sm px-3 py-1 rounded-full tracking-widest uppercase whitespace-nowrap">After</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
